import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, Landmark, Loader2, Lock, ShoppingCart } from "lucide-react";

const money = (c) =>
  c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHODS = [
  { id: "card", label: "Card", sub: "Credit or debit", icon: CreditCard },
  { id: "us_bank_account", label: "Bank transfer", sub: "ACH · lower fee", icon: Landmark },
];

// Buyer checkout confirm step: shows the sale price plus the payment-processing cost for the buyer's
// CHOSEN method (added on top), then locks the Stripe session to that method. Reused by Buy Now and
// accepted-offer checkout. The word "surcharge" is intentionally avoided (state law restrictions);
// this is simply the processing cost for the selected payment method.
export function CheckoutModal({ open, onClose, listingId, title, promoCode, variantId, offerId }) {
  const [quote, setQuote] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !listingId) return;
    setQuote(null); setLoadErr(null); setMethod("card");
    api.post("/orders/checkout-quote", {
      listing_id: listingId, origin_url: window.location.origin,
      promo_code: promoCode || undefined, variant_id: variantId || undefined, offer_id: offerId || undefined,
    }).then(({ data }) => setQuote(data))
      .catch((err) => setLoadErr(formatError(err.response?.data?.detail) || "Couldn't load checkout details."));
  }, [open, listingId, promoCode, variantId, offerId]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        listing_id: listingId, origin_url: window.location.origin,
        promo_code: promoCode || undefined, variant_id: variantId || undefined,
        offer_id: offerId || undefined, payment_method: method,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d === "connect_not_enabled") toast.error("Payments aren't fully activated on the marketplace yet — please try again soon.");
      else toast.error(formatError(d));
      setBusy(false);
    }
  };

  const sel = quote?.methods?.[method];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-surface border border-sniper max-w-md" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2 text-foreground">
            <ShoppingCart className="w-5 h-5 text-olive-bright" /> Complete purchase
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-muted-foreground truncate">
            {title}
          </DialogDescription>
        </DialogHeader>

        {loadErr ? (
          <div className="border border-blaze/50 bg-blaze/10 px-3 py-3 font-mono text-xs text-blaze" data-testid="checkout-error">{loadErr}</div>
        ) : !quote ? (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground font-mono text-xs uppercase" data-testid="checkout-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Order summary */}
            <div className="border border-[#2E2F31] bg-[#111213]">
              <div className="px-3.5 py-2.5 space-y-1.5 font-mono text-[12px]">
                <div className="flex items-center justify-between" data-testid="checkout-line-item">
                  <span className="uppercase tracking-wide text-[#8B8D90]">Item price</span>
                  <span className="text-foreground">{money(quote.item_cents + (quote.discount_cents || 0))}</span>
                </div>
                {quote.discount_cents > 0 && (
                  <div className="flex items-center justify-between" data-testid="checkout-line-discount">
                    <span className="uppercase tracking-wide text-[#8B8D90]">Promo discount</span>
                    <span className="text-olive-bright">{"\u2212"}{money(quote.discount_cents)}</span>
                  </div>
                )}
                {quote.shipping_cents > 0 && (
                  <div className="flex items-center justify-between" data-testid="checkout-line-shipping">
                    <span className="uppercase tracking-wide text-[#8B8D90]">Shipping</span>
                    <span className="text-foreground">+{money(quote.shipping_cents)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between" data-testid="checkout-line-processing">
                  <span className="uppercase tracking-wide text-[#8B8D90]">Payment processing</span>
                  <span className="text-foreground">+{money(sel?.processing_cents)}</span>
                </div>
              </div>
              <div className="border-t border-[#2E2F31] px-3.5 py-2.5 flex items-center justify-between">
                <span className="font-mono uppercase tracking-wide text-[11px] text-[#9BB37A]">You pay</span>
                <span className="font-heading text-lg text-olive-bright" data-testid="checkout-total">{money(sel?.total_cents)}</span>
              </div>
            </div>

            {/* Payment method selector — drives the processing cost + locks the Stripe session */}
            <div>
              <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Payment method</span>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {METHODS.map((m) => {
                  const Icon = m.icon;
                  const active = method === m.id;
                  const proc = quote.methods?.[m.id]?.processing_cents;
                  return (
                    <button key={m.id} type="button" onClick={() => setMethod(m.id)} data-testid={`checkout-method-${m.id}`}
                      aria-pressed={active}
                      className={`flex flex-col items-start gap-1 border px-3 py-2.5 text-left transition-colors ${active ? "border-olive bg-olive/10" : "border-sniper hover:border-foreground/40"}`}>
                      <span className={`flex items-center gap-1.5 font-heading uppercase text-sm ${active ? "text-olive-bright" : "text-foreground"}`}>
                        <Icon className="w-4 h-4" /> {m.label}
                      </span>
                      <span className="font-mono text-[10px] uppercase text-muted-foreground">{m.sub}</span>
                      <span className="font-mono text-[10px] text-[#8B8D90]">+{money(proc)} processing</span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-[#6C6F73]" data-testid="checkout-processing-note">
                Processing is the cost to process your payment for the method you choose. Bank transfer is the lower-fee option.
              </p>
              {quote.buy_label_shipping_pending && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-[#E0A93B]" data-testid="checkout-shipping-note">
                  Shipping is calculated on the next step
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={onClose} disabled={busy} data-testid="checkout-cancel-btn"
                className="flex-1 border border-sniper hover:border-foreground text-secondary-foreground hover:text-foreground font-mono uppercase text-sm py-2.5 transition-colors">
                Cancel
              </button>
              <button onClick={confirm} disabled={busy} data-testid="checkout-confirm-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue to payment</>}
              </button>
            </div>
            <p className="flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase text-muted-foreground">
              <Lock className="w-3 h-3 text-olive-bright" /> Payment protected — released after delivery is confirmed
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CheckoutModal;
